import React, {useContext, useEffect, useState} from 'react';
import {FileUploadWidgetFormFieldProps} from '@src/@types/components/Form';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import DismissibleAttach, {FileType} from '@src/components/DismissibleAttach/DismissibleAttach';
import app from '@src/helpers/app';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import Translate from '@src/components/Translate/Translate';
import ControlledInput from '../fields/ControlledInput';
import useFormValue from '@src/hooks/useFormValue';
import {IFileModel} from '@src/@interfaces/models/FIle/IFileModel';

type SelectedImageMeta = {
    isImage: boolean,
    uniqueId: string,
    img: {
        src: string | null,
        alt: string,
    },
    meta: FileType,
};

const createNewCacheStore = () => {
    const store: Map<File, {
        src: string | null,
        uniqueId: string,
    }> = new Map();

    return {
        get: store.get.bind(store),
        set: store.set.bind(store),
        entries: store.entries.bind(store),
        clear(uniqueIdsToStay: Record<string, any> = {}) {
            for (const [key, {src, uniqueId}] of store.entries()) {
                if (uniqueId in uniqueIdsToStay) {
                    continue;
                }

                if (src) {
                    URL.revokeObjectURL(src);
                }

                store.delete(key);
            }
        },
        destroy() {
            this.clear();
        }
    };
};

function toArray<T>(item: T): any[] {
    if (item == null) {
        return [];
    }

    if (Array.isArray(item)) {
        return item;
    }

    return [item];
}

export default function FileUploadFieldWidget<Model extends IEmptyModel>(props: FileUploadWidgetFormFieldProps<Model, 'image'>) {
    const dot = app('dot-accessor');

    const [cacheStore] = useState(createNewCacheStore());

    const [designContext] = useContext(FormFieldDesignContext);
    const {currentValue: value} = useFormValue(props.fieldName);
    const [error, setError] = useState<{ message: string, replacement?: Record<string, string | number> } | null>(null);
    const [hiddenInitialValues, setHiddenInitialValues] = useState<string[]>([]);
    const [previews, setPreviews] = useState<SelectedImageMeta[]>([]);

    const fieldName = props.fieldName as any;
    const isMultiple = props.config.multiple;
    const maxFileSizeInMB = props.config.maxFilesize;
    const onlyImagesAllowed = props.config.mode === 'image';
    const availableExtensions = props.config.fileTypes?.split(',').map(c => c.trim()) as (string[] | undefined);
    const initialValues: string[] | undefined = toArray(dot.get(props.model ?? {}, `${fieldName}_public_path`));

    useEffect(() => {
        let filesSizeInBites = 0;
        let hasIncorrectFileTypes = false;
        let hasNotOnlyImages = false;

        const uniqueIdsToStayAlive: Record<string, string> = {};
        const previews = (
                value instanceof FileList ?
                        [].slice.call(value) :
                        toArray(value).filter(item => item instanceof File)
        ).map<SelectedImageMeta>((file: File) => {
            filesSizeInBites += file.size;
            const isImage = file.type.split('/').shift() === 'image';
            const extension = file.name.split('.').pop() as string;

            hasNotOnlyImages = hasNotOnlyImages || !isImage;
            hasIncorrectFileTypes = hasIncorrectFileTypes || (
                    availableExtensions != undefined &&
                    !availableExtensions.includes(extension)
            );

            const cache = cacheStore.get(file);

            const src = isImage ? (cache?.src ?? URL.createObjectURL(file)) : null;
            const uniqueId = cache?.uniqueId ?? (src ?? file.name + '@' + file.size);

            uniqueIdsToStayAlive[uniqueId] = uniqueId;

            if (!cache) {
                cacheStore.set(file, {src, uniqueId});
            }

            return {
                isImage,
                uniqueId,
                img: {
                    src,
                    alt: file.name,
                },
                meta: {
                    is_image: isImage,
                    file_name: file.name,
                    file_extension: file.name.split('.').pop(),
                    file_mime_type: file.type,
                    asset_file_path: src,
                }
            };
        });

        // clear previews cache
        cacheStore.clear(uniqueIdsToStayAlive);

        setPreviews(previews);

        if (maxFileSizeInMB && maxFileSizeInMB > filesSizeInBites / 1024 / 1024) {
            setError({
                message: 'FileUploadFieldWidget.max-size-limit-exceeded',
                replacement: {
                    limit: maxFileSizeInMB,
                }
            });
        } else if (availableExtensions && hasIncorrectFileTypes) {
            setError({
                message: 'FileUploadFieldWidget.incorrect-file-type',
                replacement: {
                    availableExtensions: availableExtensions.join('; '),
                }
            });
        } else if (hasNotOnlyImages && onlyImagesAllowed) {
            setError({
                message: 'FileUploadFieldWidget.only-images-allowed',
            });
        } else {
            setError(null);
        }
    }, [value, setPreviews, maxFileSizeInMB, availableExtensions, onlyImagesAllowed, cacheStore]);

    useEffect(() => cacheStore.destroy(), [cacheStore]);

    const deleteAttachment = (src: string) => {
        // products.0.preview -> products.0.id
        const ownerId = dot.get<string>(props.model ?? {}, fieldName.replace(/(.*?\.?)([^.]+)$/uim, '$1id'));

        // products.0.previews -> previews
        const fieldActualName = fieldName.replace(/.+\.([^.]+)$/uim, '$1');

        const payload: Record<string, any> = {};
        if (isMultiple) {
            const listOfFiles = dot.get<IFileModel[] | undefined>(props.model ?? {}, fieldName);
            const attachmentId = listOfFiles?.find(item => item.asset_file_path === src)?.id;
            if (!attachmentId) {
                return Promise.reject(new Error('Cannot find an file'));
            }

            payload.attachmentId = attachmentId;
        }

        return app('http').route('abstract-models.delete-attachment', {
            id: ownerId,
        }, {
            headers: {'Content-Type': 'application/json'},
            body: {
                'controller-name': props.formName,
                attachment: fieldActualName,
                ...payload,
            }
        });
    };

    return <>
        {initialValues.length ? (
                <div className="mb-1 d-flex flex-wrap">
                    {initialValues.map((src, i) => {
                        if (hiddenInitialValues.includes(src)) {
                            return null;
                        }

                        return <DismissibleAttach
                                file={{src, alt: `#${i}`}}
                                key={src}
                                disabled={designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly}
                                size={props.config.previewSize}
                                onDismiss={(e, setDisable) => {
                                    setDisable(true);

                                    deleteAttachment(src).then(() => {
                                        setHiddenInitialValues([...hiddenInitialValues, src]);
                                    }).catch(reason => {
                                        setDisable(false);

                                        setError(typeof reason === 'string' ? reason : reason.message);
                                    });
                                }}
                        />;
                    })}
                </div>
        ) : null}

        <ControlledInput
                type="file"
                {...props}
                multiple={isMultiple}
                accept={props.config.mimeTypes}
                maxLength={props.config.maxFiles}
                isInvalid={error ? true : undefined}
                onChange={(e, next) => {
                    if (isMultiple) {
                        return void next(e.target?.files);
                    }

                    next(e.target?.files?.length ? e.target?.files[0] : null);
                }}
        />

        {error ? (
                <Translate
                        message={error.message}
                        replace={error.replacement}
                        className={designContext.errorMessageContainerClass}
                />
        ) : null}

        {previews.length ? (
                <div className="mt-1 d-flex">
                    {previews.map((item, index) => {
                        return <DismissibleAttach
                                key={`new-one-${item.uniqueId}-#${index}`}
                                file={item.isImage ? item.img : item.meta}
                                size={props.config.previewSize}
                        />;
                    })}
                </div>
        ) : null}
    </>;
}
